import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ClipboardList, Download, Eye, FileCode, Save, FileSpreadsheet, Ticket as TicketIcon, Settings as SettingsIcon, User, Plane, Building2, Car, Shield, MapPin, Calendar } from "lucide-react";

const FORM_KEY = "servicios";

const SERVICE_LABELS = {
  flight: "Vuelo", hotel: "Hotel", transfer: "Traslado",
  car: "Renta de auto", insurance: "Seguro", activity: "Actividad",
};

const FormulariosServicios = () => {
  const [tab, setTab] = useState("submissions");
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ empresa: "", email: "", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);
  const [schema, setSchema] = useState({ html: "", version: 0, updated_at: null, updated_by: null, from_alias: "", sender_user_id: "", sender_email: "" });
  const [schemaDirty, setSchemaDirty] = useState(false);
  const [aliasDirty, setAliasDirty] = useState(false);
  const [senderDirty, setSenderDirty] = useState(false);
  const [gmailSenders, setGmailSenders] = useState([]);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [versionInput, setVersionInput] = useState("");
  const [savingSchema, setSavingSchema] = useState(false);

  const fetchSubs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.empresa) params.append("empresa", filters.empresa);
      if (filters.email) params.append("email", filters.email);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/form-submissions/${FORM_KEY}${qs}`, { withCredentials: true });
      setSubs(res.data || []);
    } catch { toast.error("Error al cargar solicitudes"); }
    finally { setLoading(false); }
  }, [filters]);

  const fetchSchema = async () => {
    try {
      const res = await axios.get(`${API}/form-schemas/${FORM_KEY}`, { withCredentials: true });
      setSchema({ html: "", version: 0, from_alias: "", sender_user_id: "", sender_email: "", ...(res.data || {}) });
      setSchemaDirty(false);
      setAliasDirty(false);
      setSenderDirty(false);
    } catch { toast.error("Error al cargar schema"); }
  };

  const fetchGmailSenders = async () => {
    try {
      const res = await axios.get(`${API}/form-schemas/_gmail-senders/list`, { withCredentials: true });
      setGmailSenders(res.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchSubs(); }, [fetchSubs]);
  useEffect(() => {
    if (tab === "schema" || tab === "config") fetchSchema();
    if (tab === "config") fetchGmailSenders();
  }, [tab]);

  const downloadExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.empresa) params.append("empresa", filters.empresa);
      if (filters.email) params.append("email", filters.email);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/form-submissions/${FORM_KEY}/export/xlsx${qs}`, {
        withCredentials: true, responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : `solicitudes_${FORM_KEY}.xlsx`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a"); a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al exportar");
    }
  };

  const openSaveSchemaDialog = () => {
    if (!schemaDirty) return;
    // Sugerir un default sensato: incrementar el número si la versión actual es un entero, si no dejarlo vacío
    const cur = schema.version;
    const asInt = parseInt(cur, 10);
    setVersionInput(Number.isFinite(asInt) && String(asInt) === String(cur) ? String(asInt + 1) : "");
    setVersionDialogOpen(true);
  };

  const saveSchema = async () => {
    const v = (versionInput || "").trim();
    if (!v) { toast.error("Escribe la nueva versión"); return; }
    setSavingSchema(true);
    try {
      const res = await axios.put(
        `${API}/form-schemas/${FORM_KEY}`,
        { html: schema.html, version: v },
        { withCredentials: true },
      );
      setSchema((prev) => ({ ...prev, ...res.data }));
      setSchemaDirty(false);
      setVersionDialogOpen(false);
      toast.success(`Formulario guardado (v${res.data.version})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingSchema(false);
    }
  };

  const saveAlias = async () => {
    try {
      const res = await axios.put(`${API}/form-schemas/${FORM_KEY}`, { from_alias: schema.from_alias || "" }, { withCredentials: true });
      setSchema((prev) => ({ ...prev, ...res.data }));
      setAliasDirty(false);
      toast.success(res.data.from_alias ? `Alias guardado: ${res.data.from_alias}` : "Alias removido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar alias");
    }
  };

  const saveSender = async () => {
    try {
      const res = await axios.put(`${API}/form-schemas/${FORM_KEY}`, { sender_user_id: schema.sender_user_id || "" }, { withCredentials: true });
      setSchema((prev) => ({ ...prev, ...res.data }));
      setSenderDirty(false);
      toast.success(res.data.sender_email ? `Buzón guardado: ${res.data.sender_email}` : "Buzón removido (usará el default)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar buzón");
    }
  };

  const openDetail = async (s) => {
    try {
      const res = await axios.get(`${API}/form-submissions/${FORM_KEY}/${s.id}`, { withCredentials: true });
      setDetail(res.data);
    } catch { toast.error("Error al cargar detalle"); }
  };

  const downloadPdf = async (s) => {
    try {
      const res = await axios.get(`${API}/form-submissions/${FORM_KEY}/${s.id}/pdf`, {
        withCredentials: true, responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : `formulario_${s.id}.pdf`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar el PDF"); }
  };

  const handleCreateTicket = async (s) => {
    if (!window.confirm("¿Crear ticket Helpdesk para esta solicitud?")) return;
    try {
      const res = await axios.post(`${API}/form-submissions/${FORM_KEY}/${s.id}/create-ticket`, {}, { withCredentials: true });
      toast.success(`Ticket creado: ${res.data.ticket?.folio}`);
      fetchSubs();
      if (detail?.id === s.id) {
        const refreshed = await axios.get(`${API}/form-submissions/${FORM_KEY}/${s.id}`, { withCredentials: true });
        setDetail(refreshed.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear ticket");
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—";

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="formularios-servicios-page">
      <div className="max-w-7xl mx-auto space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="w-6 h-6" /> Formulario de Cotizaciones
          </h1>
          <p className="text-slate-500 text-sm">Solicitudes de cotización recibidas desde el formulario externo de clientes.</p>
        </div>

        <div className="flex gap-2 border-b">
          <button onClick={() => setTab("submissions")} className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${tab === "submissions" ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-500"}`} data-testid="tab-submissions">
            <ClipboardList className="w-4 h-4 inline mr-1" /> Solicitudes
          </button>
          <button onClick={() => setTab("config")} className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${tab === "config" ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-500"}`} data-testid="tab-config">
            <SettingsIcon className="w-4 h-4 inline mr-1" /> Configuración
          </button>
        </div>

        {tab === "submissions" && (
          <>
            <div className="rounded-lg border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 p-3 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <ClipboardList className="w-4 h-4 text-blue-700" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-blue-900 leading-relaxed">
                  <b>Formulario actualizado:</b> el formulario que ven tus clientes ahora es un componente React nativo del Back Office.
                  Para verlo en acción, ve a <b>Traveldesk → Formularios BTravel → Administrar accesos</b>, copia el link
                  del acceso y ábrelo en una pestaña privada.
                </p>
              </div>
            </div>
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Empresa</label>
                    <Input placeholder="Empresa" value={filters.empresa} onChange={(e) => setFilters({ ...filters, empresa: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Email solicitante</label>
                    <Input placeholder="email" value={filters.email} onChange={(e) => setFilters({ ...filters, email: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Desde</label>
                    <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Hasta</label>
                    <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
                  </div>
                  <div>
                    <Button onClick={downloadExcel} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="export-xlsx-btn">
                      <FileSpreadsheet className="w-4 h-4 mr-2" /> Descargar Excel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Folio</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Solicitante</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Servicios</TableHead>
                      <TableHead>Pax</TableHead>
                      <TableHead>Ticket Helpdesk</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Cargando...</TableCell></TableRow>
                    ) : subs.length === 0 ? (
                      <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Sin solicitudes</TableCell></TableRow>
                    ) : subs.map((s) => (
                      <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{fmt(s.created_at)}</TableCell>
                        <TableCell><code className="text-xs font-semibold text-blue-700">{s.folio || s.form_ticket_id || s.id.slice(0,8)}</code></TableCell>
                        <TableCell className="font-medium">{s.empresa}</TableCell>
                        <TableCell className="text-sm">
                          {s.requester_name}
                          <div className="text-xs text-slate-500">{s.requester_email}</div>
                        </TableCell>
                        <TableCell className="text-xs">{s.travel_type || "—"}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(s.services_list || []).map(sv => (
                              <Badge key={sv} variant="outline" className="text-[10px]">{SERVICE_LABELS[sv] || sv}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-center">{s.passenger_count || 0}</TableCell>
                        <TableCell>
                          {s.helpdesk_ticket_folio ? (
                            <a href={`/helpdesk/tickets/${s.helpdesk_ticket_id}`} className="text-blue-600 hover:underline text-xs font-mono font-semibold">{s.helpdesk_ticket_folio}</a>
                          ) : (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleCreateTicket(s)} data-testid={`create-ticket-${s.id}`}>
                              <TicketIcon className="w-3 h-3 mr-1" /> {s.pdf_storage_path ? "Crear ticket" : "Generar PDF + Ticket"}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => openDetail(s)} title="Ver detalle"><Eye className="w-4 h-4" /></Button>
                          {s.pdf_storage_path && (
                            <Button variant="ghost" size="icon" onClick={() => downloadPdf(s)} title="Descargar PDF"><Download className="w-4 h-4 text-rose-600" /></Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}

        {tab === "schema" && (
          <div className="space-y-4">
            <div className="rounded-lg border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <FileCode className="w-5 h-5 text-amber-700" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-amber-900">Este editor HTML ya no está en uso</h3>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                    A partir de esta versión, el formulario que ven los clientes en el portal está construido como
                    componente React nativo del Back Office. Ya <b>no necesitas pegar código HTML</b>.
                  </p>
                  <p className="text-xs text-amber-800 mt-2 leading-relaxed">
                    <b>¿Dónde lo pruebo?</b> Abre el portal cliente con un usuario Solicitante:
                    ve a <b>Traveldesk → Formularios BTravel → Administrar accesos</b>, copia el link del acceso y
                    ábrelo en una pestaña privada.
                  </p>
                  <p className="text-xs text-amber-800 mt-2 leading-relaxed">
                    Este editor se conserva sólo como referencia histórica; los cambios que hagas aquí <b>no afectan</b>
                    al formulario del portal. La configuración por acceso (servicios permitidos, campos bloqueados,
                    catálogos de hoteles/vuelos) se hará en el modal de creación de acceso próximamente.
                  </p>
                </div>
              </div>
            </div>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-slate-600">
                      <FileCode className="w-5 h-5" /> Código HTML del formulario (Legacy)
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                      Versión actual: <strong>v{schema.version || 0}</strong>
                      {schema.updated_at && ` · Última edición: ${fmt(schema.updated_at)}`}
                      {schema.updated_by && ` por ${schema.updated_by}`}
                    </p>
                  </div>
                  <Button onClick={openSaveSchemaDialog} disabled={!schemaDirty} variant="outline" className="text-slate-600" data-testid="save-schema-btn">
                    <Save className="w-4 h-4 mr-2" /> Guardar nueva versión
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-2 italic">
                  Sólo edita aquí si necesitas revertir a la versión HTML por alguna razón excepcional.
                </p>
                <textarea
                  value={schema.html}
                  onChange={(e) => { setSchema({ ...schema, html: e.target.value }); setSchemaDirty(true); }}
                  className="w-full h-[500px] font-mono text-xs p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 bg-slate-50"
                  placeholder="<!DOCTYPE html>..."
                  data-testid="schema-editor"
                />
              </CardContent>
            </Card>
          </div>
        )}

        {tab === "config" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <SettingsIcon className="w-5 h-5" /> Configuración general — Formulario de Cotizaciones
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Ajustes globales del formulario que afectan los correos automáticos generados al recibir una nueva solicitud.
              </p>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* 1. Buzón remitente fijo */}
              <div>
                <Label htmlFor="sender-user-select" className="text-sm font-semibold">
                  Buzón remitente fijo
                </Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Cuenta de Gmail desde la que <strong>siempre</strong> se enviarán los correos de tickets generados por este formulario,
                  ignorando el asignado/creador del ticket. Para que aparezca en la lista, el usuario debe haber conectado Gmail
                  desde su perfil (Sidebar → ícono Gmail).
                  <br />
                  Sugerencia: crea un usuario dedicado <code className="bg-slate-100 px-1 rounded">traveldesk@pdtravelmex.com</code>,
                  inicia sesión con esa cuenta una vez, conecta su Gmail, y selecciónalo aquí.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center max-w-2xl">
                  <select
                    id="sender-user-select"
                    value={schema.sender_user_id || ""}
                    onChange={(e) => { setSchema({ ...schema, sender_user_id: e.target.value }); setSenderDirty(true); }}
                    data-testid="sender-user-select"
                    className="flex-1 h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="">— Usar default (asignado/creador del ticket) —</option>
                    {gmailSenders.map((g) => (
                      <option key={g.user_id} value={g.user_id}>
                        {(g.user_name || g.user_email || g.gmail_email)} · ✉ {g.gmail_email}
                      </option>
                    ))}
                  </select>
                  <Button onClick={saveSender} disabled={!senderDirty} className="bg-blue-600 hover:bg-blue-700" data-testid="save-sender-btn">
                    <Save className="w-4 h-4 mr-2" /> Guardar
                  </Button>
                </div>
                {schema.sender_email && (
                  <p className="text-xs text-emerald-700 mt-2">
                    ✓ Buzón remitente actual: <code className="bg-emerald-50 px-1 rounded font-semibold">{schema.sender_email}</code>
                  </p>
                )}
                {gmailSenders.length === 0 && (
                  <p className="text-xs text-amber-600 mt-2">
                    ⚠ No hay cuentas Gmail conectadas. Pídele al usuario que conecte Gmail desde su perfil.
                  </p>
                )}
              </div>

              <div className="border-t pt-6">
                {/* 2. Alias opcional */}
                <Label htmlFor="from-alias-input" className="text-sm font-semibold">
                  Alias remitente (opcional)
                </Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Si el buzón configurado tiene aliases en Gmail ("Enviar como"), puedes forzar uno específico aquí
                  (p. ej. <code className="bg-slate-100 px-1 rounded">traveldesk@pdtravelmex.com</code>).
                  El alias debe estar agregado y <strong>verificado</strong> en la cuenta del buzón seleccionado arriba.
                  Si no, déjalo vacío y el correo saldrá desde la dirección principal de ese buzón.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center max-w-2xl">
                  <Input
                    id="from-alias-input"
                    type="email"
                    placeholder="traveldesk@pdtravelmex.com (opcional)"
                    value={schema.from_alias || ""}
                    onChange={(e) => { setSchema({ ...schema, from_alias: e.target.value }); setAliasDirty(true); }}
                    data-testid="from-alias-input"
                    className="flex-1"
                  />
                  <Button onClick={saveAlias} disabled={!aliasDirty} className="bg-blue-600 hover:bg-blue-700" data-testid="save-alias-btn">
                    <Save className="w-4 h-4 mr-2" /> Guardar
                  </Button>
                </div>
                {schema.from_alias && (
                  <p className="text-xs text-emerald-700 mt-2">
                    ✓ Alias actual: <code className="bg-emerald-50 px-1 rounded font-semibold">{schema.from_alias}</code>
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detalle de solicitud</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              {/* Header info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm bg-slate-50 p-3 rounded-lg">
                <div><strong>Folio cotización:</strong><br /><code className="text-xs font-bold text-blue-700">{detail.folio || detail.form_ticket_id || "—"}</code></div>
                <div><strong>Fecha recibida:</strong><br />{fmt(detail.created_at)}</div>
                <div><strong>Empresa:</strong><br />{detail.empresa}</div>
                <div><strong>Solicitante:</strong><br />{detail.requester_name}</div>
                <div><strong>Email:</strong><br />{detail.requester_email}</div>
                <div><strong>Área:</strong><br />{detail.auth_area || "—"}{detail.auth_area_other ? ` (${detail.auth_area_other})` : ""}</div>
                <div><strong>Tipo de viaje:</strong><br />{detail.travel_type || "—"}</div>
                <div><strong>Pasajeros:</strong><br />{detail.passenger_count || 0}</div>
                <div>
                  <strong>Servicios:</strong>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(detail.services_list || []).map(sv => (
                      <Badge key={sv} variant="outline" className="text-[10px]">{SERVICE_LABELS[sv] || sv}</Badge>
                    ))}
                  </div>
                </div>
                <div className="col-span-full">
                  <strong>Ticket Helpdesk:</strong>{" "}
                  {detail.helpdesk_ticket_folio ? (
                    <a href={`/helpdesk/tickets/${detail.helpdesk_ticket_id}`} className="text-blue-600 hover:underline font-mono">{detail.helpdesk_ticket_folio}</a>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <span className="text-amber-600 text-xs">Sin ticket aún</span>
                      {detail.pdf_storage_path && (
                        <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => handleCreateTicket(detail)}>
                          <TicketIcon className="w-3 h-3 mr-1" /> Crear ahora
                        </Button>
                      )}
                    </span>
                  )}
                  {detail.helpdesk_creation_error && (
                    <div className="mt-1 text-[11px] text-red-600">⚠ Auto-creación falló: {detail.helpdesk_creation_error}</div>
                  )}
                </div>
              </div>

              {/* Workflow controls — managed from Helpdesk; here just informative status */}
              {detail.helpdesk_ticket_folio && (
                <div className="border-t pt-3">
                  <p className="text-xs text-slate-500">
                    El estado y seguimiento de esta solicitud se controlan desde el ticket de Helpdesk.
                  </p>
                  <a
                    href={`/helpdesk/tickets/${detail.helpdesk_ticket_id}`}
                    className="inline-flex items-center gap-2 mt-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 text-sm font-medium"
                  >
                    <TicketIcon className="w-4 h-4" /> Abrir ticket {detail.helpdesk_ticket_folio} en Helpdesk
                  </a>
                </div>
              )}

              {/* Detalle estructurado de todos los datos del formulario */}
              <SubmissionDetailsBody data={detail.data || {}} />
              <DialogFooter>
                {detail.pdf_storage_path && (
                  <Button variant="outline" onClick={() => downloadPdf(detail)}>
                    <Download className="w-4 h-4 mr-2" /> Descargar PDF
                  </Button>
                )}
                <Button onClick={() => setDetail(null)}>Cerrar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: pedir versión al guardar HTML */}
      <Dialog open={versionDialogOpen} onOpenChange={(o) => !savingSchema && setVersionDialogOpen(o)}>
        <DialogContent className="max-w-md" data-testid="version-dialog">
          <DialogHeader>
            <DialogTitle>Guardar nueva versión</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="version-input">Etiqueta de versión</Label>
              <Input
                id="version-input"
                value={versionInput}
                onChange={(e) => setVersionInput(e.target.value)}
                placeholder="Ej. 6, 2.1, 2026-08 revA…"
                autoFocus
                disabled={savingSchema}
                data-testid="version-input"
                onKeyDown={(e) => { if (e.key === "Enter" && !savingSchema && (versionInput || "").trim()) { saveSchema(); } }}
              />
              <p className="text-xs text-slate-500 mt-1.5">
                Puedes usar números (1, 2, 3…) o texto libre (v2.1, agosto-2026, revA). Versión actual: <strong>v{schema.version || 0}</strong>.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersionDialogOpen(false)} disabled={savingSchema} data-testid="version-cancel">Cancelar</Button>
            <Button onClick={saveSchema} disabled={savingSchema || !(versionInput || "").trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="version-save">
              {savingSchema ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormulariosServicios;

// ═══════════════════════════════════════════════════════════════════
// SubmissionDetailsBody
// ═══════════════════════════════════════════════════════════════════
// Renderiza TODOS los datos capturados por el formulario de forma
// estructurada por secciones (Solicitante, Datos generales, Pasajeros,
// Vuelos, Hoteles, Transportes/Traslados, Renta de auto, Seguros,
// Actividades). Reemplaza el "Payload JSON completo" para que el
// personal interno pueda leer el detalle sin bucear en JSON.
const _Fld = ({ label, value }) => {
  const v = value === null || value === undefined ? "" : String(value).trim();
  if (!v || ["null", "undefined", "nan"].includes(v.toLowerCase())) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-slate-500 min-w-[8.5rem]">{label}:</span>
      <span className="text-slate-800 flex-1 break-words">{v}</span>
    </div>
  );
};

const _SectionCard = ({ title, icon: Icon, count, children, testid }) => {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-900 to-blue-700 text-white text-xs font-bold uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="flex-1">{title}</span>
        {typeof count === "number" && count > 1 && (
          <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">{count}</span>
        )}
      </div>
      <div className="p-3 space-y-2">{children}</div>
    </div>
  );
};

const _ItemBlock = ({ number, children }) => (
  <div className="border border-slate-200 rounded p-2.5 bg-slate-50 space-y-1">
    {number && (
      <div className="text-[11px] font-bold uppercase text-indigo-700 tracking-wider mb-1">#{number}</div>
    )}
    {children}
  </div>
);

const _hasAny = (obj, keys) => keys.some((k) => {
  const v = obj?.[k];
  if (v === null || v === undefined) return false;
  const s = String(v).trim();
  return s && !["null", "undefined", "nan"].includes(s.toLowerCase());
});

const SubmissionDetailsBody = ({ data }) => {
  const requester = data.requester || {};
  const passengers = data.passengers || [];
  const services = data.services || [];
  const flights = data.flights || [];
  const hotels = data.hotels || [];
  const transfers = data.transfers || [];
  const carRentals = data.carRentals || [];
  const insurances = data.travelInsurances || [];
  const activities = data.activities || [];

  const serviceLabelMap = {
    flight: "Vuelo", hotel: "Hotel", transfer: "Transporte / Traslado",
    car: "Renta de auto", insurance: "Seguro de viaje", activity: "Actividades / Tours",
  };

  return (
    <div className="border-t pt-3 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="w-4 h-4 text-indigo-700" />
        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Datos capturados en el formulario</h3>
      </div>

      {/* Solicitante */}
      {_hasAny(requester, ["name", "email", "phone", "authArea", "companyName"]) && (
        <_SectionCard title="Solicitante" icon={User} testid="detail-section-solicitante">
          <_Fld label="Nombre" value={requester.name} />
          <_Fld label="Correo" value={requester.email} />
          <_Fld label="Teléfono" value={requester.phone} />
          <_Fld label="Empresa" value={requester.companyName} />
          <_Fld label="Área de autorización" value={requester.authArea === "Otro" && requester.authAreaOther ? `${requester.authArea} (${requester.authAreaOther})` : requester.authArea} />
        </_SectionCard>
      )}

      {/* Datos generales */}
      <_SectionCard title="Datos generales del viaje" icon={Calendar} testid="detail-section-general">
        <_Fld label="Tipo de viaje" value={data.travelType} />
        <_Fld label="Nº de pasajeros" value={passengers.length || null} />
        {services.length > 0 && (
          <div className="flex items-start gap-2 text-sm">
            <span className="text-slate-500 min-w-[8.5rem]">Servicios solicitados:</span>
            <div className="flex flex-wrap gap-1 flex-1">
              {services.map((s) => (
                <Badge key={s} className="bg-indigo-100 text-indigo-800 border border-indigo-200 text-[11px]">
                  {serviceLabelMap[s] || s}
                </Badge>
              ))}
            </div>
          </div>
        )}
        <_Fld label="Empresa" value={data.company} />
        <_Fld label="Fecha de captura" value={data.timestamp ? new Date(data.timestamp).toLocaleString("es-MX") : ""} />
      </_SectionCard>

      {/* Pasajeros */}
      {passengers.length > 0 && (
        <_SectionCard title="Pasajeros" icon={User} count={passengers.length} testid="detail-section-pasajeros">
          {passengers.map((p, i) => (
            <_ItemBlock key={i} number={p.index || i + 1}>
              <_Fld label="Nombre completo" value={p.fullName} />
              <_Fld label="Fecha de nacimiento" value={p.birthDate} />
              <_Fld label="Correo" value={p.email} />
              <_Fld label="Teléfono" value={p.phone} />
              {p.doc && (p.doc.type || p.doc.number) && (
                <_Fld label="Documento" value={[p.doc.type, p.doc.number].filter(Boolean).join(" · ")} />
              )}
            </_ItemBlock>
          ))}
        </_SectionCard>
      )}

      {/* Vuelos */}
      {flights.length > 0 && (
        <_SectionCard title="Vuelos" icon={Plane} count={flights.length} testid="detail-section-vuelos">
          {flights.map((f, i) => (
            <_ItemBlock key={i} number={i + 1}>
              <_Fld label="Ruta" value={[f.origin, f.destination].filter(Boolean).join(" → ")} />
              <_Fld label="Tipo" value={f.type} />
              <_Fld label="Clase" value={f.class} />
              <_Fld label="Salida" value={[f.departureDate, f.departureTime].filter(Boolean).join(" ")} />
              <_Fld label="Regreso" value={[f.returnDate, f.returnTime].filter(Boolean).join(" ")} />
              <_Fld label="Aerolínea preferida" value={f.preferredAirline} />
              <_Fld label="Notas" value={f.notes} />
            </_ItemBlock>
          ))}
        </_SectionCard>
      )}

      {/* Hoteles */}
      {hotels.length > 0 && (
        <_SectionCard title="Hoteles" icon={Building2} count={hotels.length} testid="detail-section-hoteles">
          {hotels.map((h, i) => (
            <_ItemBlock key={i} number={i + 1}>
              <_Fld label="Ciudad" value={h.city} />
              <_Fld label="Check-in" value={h.checkIn} />
              <_Fld label="Check-out" value={h.checkOut} />
              <_Fld label="Noches" value={h.nights} />
              <_Fld label="Tipo de habitación" value={h.roomType} />
              <_Fld label="Cadena preferida" value={h.preferredChain} />
              <_Fld label="Marca preferida" value={h.preferredBrand} />
              <_Fld label="Notas" value={h.notes} />
            </_ItemBlock>
          ))}
        </_SectionCard>
      )}

      {/* Transportes / Traslados */}
      {transfers.length > 0 && (
        <_SectionCard title="Transportes / Traslados" icon={MapPin} count={transfers.length} testid="detail-section-transportes">
          {transfers.map((t, i) => (
            <_ItemBlock key={i} number={i + 1}>
              <_Fld label="Tipo" value={t.type || t.serviceType} />
              <_Fld label="Origen" value={t.origin || t.from || t.pickup?.place} />
              <_Fld label="Destino" value={t.destination || t.to || t.dropoff?.place} />
              <_Fld label="Fecha pickup" value={t.pickup?.date || t.date} />
              <_Fld label="Hora pickup" value={t.pickup?.time || t.time} />
              <_Fld label="Pasajeros" value={t.passengers || t.pax} />
              <_Fld label="Vehículo preferido" value={t.preferredVehicle || t.vehicleType} />
              <_Fld label="Notas" value={t.notes} />
            </_ItemBlock>
          ))}
        </_SectionCard>
      )}

      {/* Renta de auto */}
      {carRentals.length > 0 && (
        <_SectionCard title="Renta de auto" icon={Car} count={carRentals.length} testid="detail-section-autos">
          {carRentals.map((c, i) => (
            <_ItemBlock key={i} number={i + 1}>
              <_Fld label="Ciudad / Aeropuerto" value={c.city || c.location} />
              <_Fld label="Recolección" value={[c.pickupDate, c.pickupTime].filter(Boolean).join(" ")} />
              <_Fld label="Devolución" value={[c.returnDate, c.returnTime].filter(Boolean).join(" ")} />
              <_Fld label="Tipo de vehículo" value={c.vehicleType || c.category} />
              <_Fld label="Compañía preferida" value={c.preferredCompany || c.company} />
              <_Fld label="Notas" value={c.notes} />
            </_ItemBlock>
          ))}
        </_SectionCard>
      )}

      {/* Seguros */}
      {insurances.length > 0 && (
        <_SectionCard title="Seguros de viaje" icon={Shield} count={insurances.length} testid="detail-section-seguros">
          {insurances.map((s, i) => (
            <_ItemBlock key={i} number={i + 1}>
              <_Fld label="Cobertura" value={s.coverage || s.type} />
              <_Fld label="Fecha inicio" value={s.startDate} />
              <_Fld label="Fecha fin" value={s.endDate} />
              <_Fld label="Destinos" value={Array.isArray(s.destinations) ? s.destinations.join(", ") : s.destinations} />
              <_Fld label="Notas" value={s.notes} />
            </_ItemBlock>
          ))}
        </_SectionCard>
      )}

      {/* Actividades */}
      {activities.length > 0 && (
        <_SectionCard title="Actividades / Tours" icon={MapPin} count={activities.length} testid="detail-section-actividades">
          {activities.map((a, i) => (
            <_ItemBlock key={i} number={i + 1}>
              <_Fld label="Actividad / Tour" value={a.name || a.title || a.activity} />
              <_Fld label="Ciudad / Ubicación" value={a.city || a.location} />
              <_Fld label="Fecha" value={a.date} />
              <_Fld label="Personas" value={a.pax || a.participants} />
              <_Fld label="Notas" value={a.notes} />
            </_ItemBlock>
          ))}
        </_SectionCard>
      )}
    </div>
  );
};
